import saloon from './zones/splat.json'
import hatchback from './zones/splat-hatchback.json'
import estate from './zones/splat-estate.json'
import pickup from './zones/splat-pickup.json'
import lcv from './zones/splat-lcv.json'
import hgv from './zones/splat-hgv.json'
import hgvTractor from './zones/splat-hgv-tractor.json'
import trailer from './zones/splat-trailer.json'

const zoneMaps = {
	splat: saloon,
	'splat-hatchback': hatchback,
	'splat-estate': estate,
	'splat-pickup': pickup,
	'splat-lcv': lcv,
	'splat-hgv': hgv,
	'splat-hgv-tractor': hgvTractor,
	'splat-trailer': trailer,
}

// Splat images are 600x400, so 1% of width is 6px and 1% of height is 4px.
// A tap is ambiguous when points within EDGE_PX of it resolve to a different component.
const PX_X = 6
const PX_Y = 4
const EDGE_PX = 2
const NEARBY_PX = 10
const RING_STEPS = 12
const MAX_CLEARANCE_PX = 8

const insidePolygon = (x, y, polygon) => {
	let inside = false
	for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
		const [xi, yi] = polygon[i]
		const [xj, yj] = polygon[j]
		if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
			inside = !inside
		}
	}
	return inside
}

const insideZone = (zone, x, y) => {
	if (zone.ellipse) {
		const { cx, cy, rx, ry } = zone.ellipse
		return ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1
	}
	return insidePolygon(x, y, zone.polygon)
}

const zoneArea = (zone) => {
	if (zone.ellipse)
		return Math.PI * zone.ellipse.rx * zone.ellipse.ry

	let area = 0
	for (let i = 0, j = zone.polygon.length - 1; i < zone.polygon.length; j = i++) {
		area += (zone.polygon[j][0] + zone.polygon[i][0]) * (zone.polygon[j][1] - zone.polygon[i][1])
	}
	return Math.abs(area / 2)
}

const areas = new WeakMap()

// Where zones overlap, higher priority wins (wheels are drawn over their arches), then the smallest,
// so a lamp or handle drawn on a panel takes precedence over the panel
const outranks = (zone, other) => {
	const diff = (zone.priority || 0) - (other.priority || 0)
	if (diff) return diff > 0
	if (!areas.has(zone)) areas.set(zone, zoneArea(zone))
	if (!areas.has(other)) areas.set(other, zoneArea(other))
	return areas.get(zone) < areas.get(other)
}

const zoneAt = (map, x, y) => {
	let best = null
	for (const zone of map.zones) {
		if (insideZone(zone, x, y) && (!best || outranks(zone, best))) best = zone
	}
	return best
}

const ring = (x, y, radiusPx) => Array.from({ length: RING_STEPS }, (_, i) => {
	const angle = (2 * Math.PI * i) / RING_STEPS
	return [x + (Math.cos(angle) * radiusPx) / PX_X, y + (Math.sin(angle) * radiusPx) / PX_Y]
})

const isKnownComponent = (gradeValues, zone) =>
	!!zone?.component && (!gradeValues || !!gradeValues.Exterior?.[zone.category]?.[zone.component])

const pick = zone => ({ category: zone.category, component: zone.component })

export const hasZoneMap = (splat) => !!zoneMaps[splat]

/**
 * Suggest a NAMA category/component for a tap on the splat image (x/y in % of the image).
 * Returns { suggestion, alternatives }: suggestion is only set when the tap is clearly inside one
 * zone; alternatives lists other nearby components so the UI can offer a quick pick.
 */
export const suggestComponent = (splat, x, y, gradeValues = null) => {
	const empty = { suggestion: null, alternatives: [] }
	const map = zoneMaps[splat]

	if (!map || x == null || y == null) return empty

	const hit = zoneAt(map, x, y)

	if (hit && !hit.component) return empty

	const edgeZones = ring(x, y, EDGE_PX).map(([px, py]) => zoneAt(map, px, py))
	const nearbyZones = ring(x, y, NEARBY_PX).map(([px, py]) => zoneAt(map, px, py))

	// A zone's own alternatives (e.g. Wheel Trim for a Wheel) rank ahead of neighbouring zones
	const alternatives = []
	for (const zone of [hit, ...(hit?.alternatives || []), ...edgeZones, ...nearbyZones]) {
		if (!isKnownComponent(gradeValues, zone)) continue
		if (alternatives.some(a => a.component === zone.component)) continue
		alternatives.push(pick(zone))
	}

	const clear = isKnownComponent(gradeValues, hit)
		&& edgeZones.every(zone => !zone || zone.component === hit.component)

	if (clear) {
		return {
			suggestion: pick(hit),
			alternatives: alternatives.filter(a => a.component !== hit.component),
		}
	}

	return { suggestion: null, alternatives: alternatives.slice(0, 3) }
}

// How far (px, up to max) you can move from a point before it stops resolving to the component
const clearance = (map, x, y, component, max) => {
	for (let r = 1; r <= max; r++) {
		if (ring(x, y, r).some(([px, py]) => zoneAt(map, px, py)?.component !== component))
			return r - 1
	}
	return max
}

/**
 * The whole-% point deepest inside a component's zone, used to move markers when the splat changes.
 */
export const componentPosition = (splat, component) => {
	const map = zoneMaps[splat]
	const zones = map?.zones.filter(z => z.component === component) || []

	if (!zones.length) return null

	const zone = zones[0]
	const [minX, maxX, minY, maxY] = zone.ellipse
		? [zone.ellipse.cx - zone.ellipse.rx, zone.ellipse.cx + zone.ellipse.rx, zone.ellipse.cy - zone.ellipse.ry, zone.ellipse.cy + zone.ellipse.ry]
		: [
			Math.min(...zone.polygon.map(([px]) => px)), Math.max(...zone.polygon.map(([px]) => px)),
			Math.min(...zone.polygon.map(([, py]) => py)), Math.max(...zone.polygon.map(([, py]) => py)),
		]

	let best = null
	for (let x = Math.ceil(minX); x <= maxX; x++) {
		for (let y = Math.ceil(minY); y <= maxY; y++) {
			if (zoneAt(map, x, y)?.component !== component) continue
			const c = clearance(map, x, y, component, MAX_CLEARANCE_PX)
			if (!best || c > best.c) best = { x, y, c }
			if (c === MAX_CLEARANCE_PX) return { x, y }
		}
	}

	return best ? { x: best.x, y: best.y } : null
}
